import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTranslation } from "react-i18next";

/**
 * ProcessingScreen - Step 3: Animated progress synced with real OCR API call.
 *
 * @param {boolean} isApiDone - Whether the OCR API has finished successfully
 * @param {string|null} apiError - Error message if API failed, null otherwise
 * @param {Function} onComplete - Called when animation finishes after API success
 * @param {Function} onError - Called when API error is detected
 */
const ProcessingScreen = ({ isApiDone, apiError, onComplete, onError, isCatalog = false }) => {
  const { t } = useTranslation("dashboard");
  const label = isCatalog ? t("catalogue", "catalogue") : t("menu", "menu");
  const [progress, setProgress] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  const processingSteps = [
    { id: 1, label: t("ocrProcessingStep1") },
    { id: 2, label: t("ocrProcessingStep2") },
    { id: 3, label: t("ocrProcessingStep3") },
    { id: 4, label: t("ocrProcessingStep4") },
  ];

  // Simulated progress: advances quickly to ~85%, then slows down until API finishes
  useEffect(() => {
    if (apiError) return;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 85) {
          return Math.min(prev + 0.2, 95);
        }
        return prev + (85 / (4000 / 50));
      });
    }, 50);

    return () => clearInterval(timer);
  }, [apiError]);

  // Handle API completion: jump to 100% and trigger onComplete
  useEffect(() => {
    if (isApiDone && !apiError) {
      setProgress(100);
      setActiveStepIndex(4);
      const timeout = setTimeout(() => onCompleteRef.current(), 500);
      return () => clearTimeout(timeout);
    }
  }, [isApiDone, apiError]);

  // Handle API error
  useEffect(() => {
    if (apiError) {
      onErrorRef.current(apiError);
    }
  }, [apiError]);

  // Advance step indicators based on progress
  useEffect(() => {
    if (progress < 20) setActiveStepIndex(0);
    else if (progress < 45) setActiveStepIndex(1);
    else if (progress < 70) setActiveStepIndex(2);
    else if (progress < 100) setActiveStepIndex(3);
  }, [progress]);

  const getStepState = (index) => {
    if (index < activeStepIndex) return "done";
    if (index === activeStepIndex) return "active";
    return "pending";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: { xs: 2, sm: 4 },
      }}
    >
      {/* Circular progress indicator */}
      <Box sx={{ position: "relative", mb: 3 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={120}
          thickness={4}
          sx={{ color: "#E9E3FF" }}
        />
        <CircularProgress
          variant="determinate"
          value={Math.round(progress)}
          size={120}
          thickness={4}
          sx={{
            color: "#7C3AED",
            position: "absolute",
            left: 0,
            top: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            {Math.round(progress)}%
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "#9ca3af",
              textTransform: "lowercase",
            }}
          >
            {t("ocrProcessingAnalyzing")}
          </Typography>
        </Box>
      </Box>

      {/* Processing title */}
      <Typography
        sx={{
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
          fontWeight: 700,
          color: "#111827",
          textTransform: "lowercase",
          mb: 0.5,
        }}
      >
        {t("ocrProcessingTitle", { label })}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8rem",
          color: "#9ca3af",
          textTransform: "lowercase",
          textAlign: "center",
          mb: 3,
          maxWidth: 320,
        }}
      >
        {t("ocrProcessingSubtitle", { label })}
      </Typography>

      {/* Step indicators grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
          width: "100%",
          maxWidth: 480,
        }}
      >
        {processingSteps.map((step, index) => {
          const state = getStepState(index);

          return (
            <Box
              key={step.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: state === "active" ? "#F3EEFF" : "#fff",
                border:
                  state === "active"
                    ? "1px solid #E9E3FF"
                    : "1px solid transparent",
                transition: "all 0.3s ease",
              }}
            >
              {state === "done" && (
                <CheckCircleIcon sx={{ fontSize: 20, color: "#7C3AED" }} />
              )}
              {state === "active" && (
                <CircularProgress size={18} sx={{ color: "#7C3AED" }} />
              )}
              {state === "pending" && (
                <RadioButtonUncheckedIcon
                  sx={{ fontSize: 20, color: "#d1d5db" }}
                />
              )}

              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: state === "active" ? 600 : 500,
                  color:
                    state === "pending"
                      ? "#9ca3af"
                      : state === "active"
                      ? "#7C3AED"
                      : "#111827",
                  textTransform: "lowercase",
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Bottom message */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "#111827",
            textTransform: "lowercase",
            mb: 0.25,
          }}
        >
          {t("ocrProcessingMayTake")}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "#9ca3af",
            textTransform: "lowercase",
          }}
        >
          {t("ocrProcessingKeepOpen", { label })}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProcessingScreen;
